import {
	type Control,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "./passwordInput";

interface TextControlProps<FormType extends FieldValues>
	extends Omit<React.ComponentProps<typeof Input>, "name"> {
	name: TypedFieldPaths<FormType, string>;
	control: Control<FormType>;
	placeholder: string;
	label: string;
	type: "text" | "email" | "password" | "tel";
}
export function TextControl<T extends FieldValues>(
	allProps: TextControlProps<T>,
) {
	const { control, name, label, ...props } = allProps;
	const InputComponent = props.type == "password" ? PasswordInput : Input;
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<FormLabel>{label}</FormLabel>
					<FormControl>
						<InputComponent
							{...field}
							{...props}
							onBlur={(event) => {
								if (props.type === "tel") {
									// Remove spaces and dashes
									event.target.value = event.target.value.replace(/[ -]/g, "");
									field.onChange(event);
								}
							}}
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}

export function CheckboxControl<FormType extends FieldValues>(props: {
	name: TypedFieldPaths<FormType, boolean | undefined>;
	control: Control<FormType>;
	label: string | React.ReactNode;
}) {
	return (
		<FormField
			control={props.control}
			name={props.name}
			render={({ field }) => (
				<FormItem>
					<FormControl>
						<Label>
							<Checkbox
								{...field}
								className="mr-2"
								checked={field.value ?? false}
								onCheckedChange={(checked) => {
									field.onChange(checked);
								}}
							/>
							{props.label}
						</Label>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}

export type TypedFieldPaths<T extends FieldValues, Type> = FieldPath<T> &
	keyof {
		[K in keyof T as T[K] extends Type ? K : never]: unknown;
	};
